/**
 * A KONTÍR-PUSKA ÁLLAPOTA — betöltés, szerkesztés, mentés.
 *
 * ═══ MIKOR MENT ═══
 *
 * A puska nem űrlap: a könyvelő FOLYAMATOSAN írja, és nem akar „Mentés"
 * gombot keresni. Ezért a mentés a MEZŐ ELHAGYÁSAKOR megy — az a pillanat,
 * amikor a gondolat lezárult, és amikor a billentyűzet-első munkában úgyis
 * `Tab` következik.
 *
 * ⚠️ ÉS BIZONYLATVÁLTÁSKOR KI KELL ÜRÍTENI. A sor-mód `Enter`-e ugrik a
 * következő papírra, és az az esemény NEM megy át fókuszvesztésen: a
 * `focus()` a kupac során történik, nem a puska mezőin. Kiürítés nélkül a
 * könyvelő utolsó gondolata a levegőben maradna, és a következő betöltés
 * felülírná — CSENDES ADATVESZTÉS, pont az, ami ellen a 2.6/22 is íródott.
 * A `documentId` váltását ezért egy effekt figyeli, és a RÉGI bizonylat
 * kéréseit még az új betöltése előtt elküldi.
 *
 * ═══ SORONKÉNT, SORBAN ═══
 *
 * A mentés nem a puska egészét küldi, hanem a KÜLÖNBSÉGET: törölt sor →
 * `DELETE`, átírt mező → `PATCH`, új sor → `POST` (`planWrites`). Ezért marad
 * meg a sorok azonosítója, és ehhez kötődik a tűzött jegyzet (#64).
 *
 * ⚠️ AZ ÍRÁSOK EGY LÁNCON FUTNAK, egymás után. Párhuzamosan két gyors `blur`
 * ugyanazt az új sort kétszer küldené `POST`-tal: a második a válasz előtt
 * indulna, amikor a sornak még nincs azonosítója. A láncon a második kör
 * már a `POST` válaszából kapott azonosítót látja, és `PATCH`-et küld. A
 * betöltés is a lánc VÉGÉRE áll: egy félbemaradt írás előtti állapot
 * betöltve a következő kör visszacsinálná az írást.
 *
 * ═══ AMI SZÁNDÉKOSAN NINCS ═══
 *
 * NINCS optimista mentés visszagörgetéssel. A sor-mód azért optimista, mert
 * ott az `Enter` naponta több száz leütés, és minden megerősítésre várás egy
 * hálózati kört épít a munkába (2.6/22). A puska mentése MÁS: nem lép tovább
 * semmi, a könyvelő ott marad a mezőnél. Elveszett mentésnél nincs mit
 * visszagörgetni — a szöveg ott van a mezőben, és az újrapróbálás
 * ugyanabból az állapotból indul.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';
import {
  dropEmptyRows,
  emptyRow,
  isFilled,
  noteFieldId,
  pinNumbers,
  planWrites,
  sumAmounts,
  toDraft,
  type Write,
} from './sheet';
import type { BookingProposalSheet, DraftRow, ProposalPin } from './types';

export type SheetStatus = 'loading' | 'failed' | 'ready';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

export interface BookingProposalsState {
  status: SheetStatus;
  rows: DraftRow[];
  /** A bizonylat bruttója — a mérce. */
  grossAmount: string | null;
  /** A sorok futó összege, fillérre pontosan. `null`, ha valamelyik alakja rossz. */
  allocated: string | null;
  saving: SaveStatus;
  setCell: (key: string, field: 'accountCode' | 'amount' | 'note', value: string) => void;
  /** A sor törlése — ez maga ment, nem kell utána `commit`. */
  removeRow: (key: string) => void;
  /** A mező elhagyása — ez ment, ha van mit. */
  commit: () => void;
  /** A jelölők száma, vázlatkulcsonként — csak a tűzött sorok kapnak. */
  pinNumbers: Map<string, number>;
  /**
   * ÚJ, TŰZÖTT SOR — még NEM megy ki. A könyvelő a jegyzetét írja hozzá, és
   * a sor a jegyzettel EGYÜTT, egy `POST`-tal megy ki a mező elhagyásakor.
   * A visszaadott kulcs a jegyzet mezőjéhez visz.
   */
  placePin: (pin: ProposalPin) => string | null;
  /** A jelölő áthelyezése vagy levétele (`null`) — ez maga ment, egy `PATCH`. */
  movePin: (key: string, pin: ProposalPin | null) => void;
  reload: () => void;
}

/** A még nem mentett sorok kulcsa. Nem ütközhet a szerver UUID-jaival. */
let draftCounter = 0;
function nextKey(): string {
  draftCounter += 1;
  return `draft-${draftCounter}`;
}

/**
 * A LISTA VÉGÉN MINDIG ÁLL EGY ÜRES SOR.
 *
 * Így nincs „új sor" gomb, amit meg kell találni: a könyvelő `Tab`-bal
 * beleér, és gépel. A teljesen üres sor nem megy ki a szervernek
 * (`planWrites`), tehát ez nem szemetel a táblában.
 */
function withTrailingBlank(rows: DraftRow[]): DraftRow[] {
  const last = rows[rows.length - 1];
  if (last && !isFilled(last)) {
    return rows;
  }
  return [...rows, emptyRow(nextKey())];
}

/**
 * EGY KÉRÉS FELSŐ IDEJE A LÁNCON.
 *
 * A betöltés és az írás minden bizonylatra EGY láncon fut, és az `api()`
 * magától nem ad fel. Időkorlát nélkül egyetlen beragadt kérés az összes
 * következő bizonylatot „betöltés"-ben hagyná, újrapróbálás nélkül.
 */
export const REQUEST_TIMEOUT_MS = 15_000;

/** A `setTimeout`-os megszakítás szándékos: az `AbortSignal.timeout` órája tesztben nem léptethető. */
async function withTimeout<T>(call: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await call(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

function base(documentId: string): string {
  return `/documents/${documentId}/booking-proposals`;
}

function fetchSheet(documentId: string): Promise<BookingProposalSheet> {
  return withTimeout((signal) => api(base(documentId), { signal }));
}

function send(documentId: string, write: Write): Promise<BookingProposalSheet> {
  return withTimeout((signal) => {
    switch (write.kind) {
      case 'delete':
        return api(`${base(documentId)}/${write.rowId}`, { method: 'DELETE', signal });
      case 'patch':
        return api(`${base(documentId)}/${write.rowId}`, {
          method: 'PATCH',
          body: JSON.stringify(write.values),
          signal,
        });
      case 'post':
        return api(base(documentId), {
          method: 'POST',
          body: JSON.stringify(write.values),
          signal,
        });
    }
  });
}

/** A vázlatsor azonosítója — a sajátja, vagy amit egy korábbi `POST` válasza adott neki. */
function idOf(row: DraftRow, assigned: Map<string, string>): string | null {
  return row.id ?? assigned.get(row.key) ?? null;
}

function withAssignedIds(drafts: DraftRow[], assigned: Map<string, string>): DraftRow[] {
  return drafts.map((row) => ({ ...row, id: idOf(row, assigned) }));
}

/**
 * A SOR MEGKAPJA AZ AZONOSÍTÓJÁT, de a KULCSA marad: kulcsváltás újramountolná
 * a mezőt, és a kurzor gépelés közben ugrana el. A szerveren már nem álló
 * sor azonosítója `null` lesz — a következő írás új sorként küldi.
 */
function withConfirmedIds(
  rows: DraftRow[],
  saved: BookingProposalSheet,
  assigned: Map<string, string>
): DraftRow[] {
  const present = new Set(saved.rows.map((row) => row.id));
  return rows.map((row) => {
    const rowId = idOf(row, assigned);
    const next = rowId !== null && present.has(rowId) ? rowId : null;
    return next === row.id ? row : { ...row, id: next };
  });
}

type Outcome = 'done' | 'expired' | 'stale' | 'failed';

/**
 * AZ ÍRÁSOK, EGYMÁS UTÁN. Minden válasz a TELJES puska — az lesz az új
 * „ismert" állapot, és a `POST` válaszából az új sor azonosítója. Az első
 * hibánál megáll: a többi írás a hibás előtti állapotra épülne.
 *
 * A SOR `404`-E NEM HIBA. Valaki más (vagy egy még régi admin cseréje)
 * közben eltüntette a sort:
 *   • a `DELETE`-nél ez maga a kívánt állapot — megyünk tovább;
 *   • a `PATCH`-nél az ismert állapot elavult (`stale`): a hívó újraolvassa,
 *     és UGYANAZOKKAL a sorokkal újratervez — az eltűnt sorból `POST` lesz,
 *     a gépelés megmarad.
 */
async function applyWrites(
  documentId: string,
  writes: Write[],
  known: Map<string, BookingProposalSheet>,
  assigned: Map<string, string>
): Promise<Outcome> {
  for (const write of writes) {
    const before = new Set((known.get(documentId)?.rows ?? []).map((row) => row.id));
    try {
      const saved = await send(documentId, write);
      known.set(documentId, saved);
      const created = saved.rows.find((row) => !before.has(row.id));
      if (write.kind === 'post' && created) {
        assigned.set(write.key, created.id);
      }
    } catch (caught: unknown) {
      const outcome = failureOf(caught, write);
      if (outcome !== 'gone') {
        return outcome;
      }
      forget(known, documentId, write);
    }
  }
  return 'done';
}

function failureOf(caught: unknown, write: Write): Outcome | 'gone' {
  if (!(caught instanceof ApiError)) {
    return 'failed';
  }
  if (caught.status === 401) {
    return 'expired';
  }
  if (caught.status !== 404) {
    return 'failed';
  }
  if (write.kind === 'delete') {
    return 'gone';
  }
  return write.kind === 'patch' ? 'stale' : 'failed';
}

/** A már nem létező sor kikerül az ismert állapotból — különben a következő kör újra törölné. */
function forget(known: Map<string, BookingProposalSheet>, documentId: string, write: Write): void {
  const sheet = known.get(documentId);
  if (sheet !== undefined && write.kind === 'delete') {
    known.set(documentId, { ...sheet, rows: sheet.rows.filter((row) => row.id !== write.rowId) });
  }
}

/**
 * ÍRÁS, és ha az ismert állapot elavult, EGY újratervezés. Egy, nem több: ha
 * a friss állapottal is elavult, ott valami más baj van, és végtelen kör
 * helyett hibát mutatunk.
 */
async function writeDrafts(
  documentId: string,
  drafts: DraftRow[],
  known: Map<string, BookingProposalSheet>,
  assigned: Map<string, string>,
  onStart: () => void
): Promise<Outcome | 'nothing'> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const writes = planWrites(withAssignedIds(drafts, assigned), known.get(documentId) ?? null);
    if (writes.length === 0) {
      return attempt === 0 ? 'nothing' : 'done';
    }
    if (attempt === 0) {
      onStart();
    }
    const outcome = await applyWrites(documentId, writes, known, assigned);
    if (outcome !== 'stale') {
      return outcome;
    }
    const refreshed = await refresh(documentId, known);
    if (refreshed !== 'done') {
      return refreshed;
    }
  }
  return 'failed';
}

/** Az elavult ismert állapot helyére a szerver mostanija. */
async function refresh(
  documentId: string,
  known: Map<string, BookingProposalSheet>
): Promise<Outcome> {
  try {
    known.set(documentId, await fetchSheet(documentId));
    return 'done';
  } catch (caught: unknown) {
    return caught instanceof ApiError && caught.status === 401 ? 'expired' : 'failed';
  }
}

export function useBookingProposals(documentId: string | null): BookingProposalsState {
  const { signOut } = useAuth();
  const [sheet, setSheet] = useState<BookingProposalSheet | null>(null);
  const [rows, setRows] = useState<DraftRow[]>([]);
  /**
   * MELYIK BIZONYLATÉ A `rows`. Bizonylatváltáskor a `rows` a betöltésig a
   * RÉGI papíré marad — és az írás-terv a sorokat a bizonylat szerverállapotához
   * méri. Ha egy még be sem töltött bizonylatot hagyunk el (A → B → C, gyorsan),
   * az A sorai B ellen tervezve B ÖSSZES sorát törölnék. Írás csak akkor megy,
   * ha a sorok tényleg annak a bizonylatnak a sorai, amelyikre írnánk.
   */
  const [rowsFor, setRowsFor] = useState<string | null>(null);
  const [status, setStatus] = useState<SheetStatus>('loading');
  const [saving, setSaving] = useState<SaveStatus>('idle');
  const generation = useRef(0);

  /**
   * A MENTÉSHEZ KELLŐ ÁLLAPOT REFBEN IS.
   *
   * A kiürítés (`documentId` váltás) egy effekt TAKARÍTÓ ÁGÁBÓL fut, ahol a
   * záradék a RÉGI renderét látná. Ref-ből olvasva viszont mindig a
   * LEGFRISSEBB sorokat küldjük, és pont az utolsó leütés a tét.
   */
  const latest = useRef<{ documentId: string | null; rows: DraftRow[]; rowsFor: string | null }>({
    documentId: null,
    rows: [],
    rowsFor: null,
  });
  latest.current = { documentId, rows, rowsFor };

  /**
   * AMIT A SZERVER UTOLJÁRA MONDOTT — bizonylatonként.
   *
   * Az írás-terv ehhez hasonlít, nem a `sheet` állapothoz: egy elhagyott
   * bizonylat kimenő írásai nem írják a `sheet`-et (az már a következő
   * papíré), a láncon viszont a következő körnek látnia kell őket.
   */
  const known = useRef(new Map<string, BookingProposalSheet>());

  /**
   * A `POST` VÁLASZÁBÓL KAPOTT AZONOSÍTÓ — vázlatkulcsonként.
   *
   * A láncra tett kör a sorokat a FELTÉTELKOR rögzíti, tehát egy még a
   * `POST` előtt sorba állt kör sorában az azonosító `null`. Innen kapja meg,
   * különben ugyanazt a sort másodszor is létrehozná.
   */
  const assigned = useRef(new Map<string, string>());

  const chain = useRef<Promise<void>>(Promise.resolve());

  const load = useCallback(
    (id: string) => {
      const mine = generation.current + 1;
      generation.current = mine;
      setStatus('loading');

      chain.current = chain.current
        .then(() => fetchSheet(id))
        .then((loaded) => {
          known.current.set(id, loaded);
          if (mine !== generation.current) {
            return;
          }
          setSheet(loaded);
          setRows(withTrailingBlank(toDraft(loaded)));
          setRowsFor(id);
          setStatus('ready');
          setSaving('idle');
        })
        .catch((caught: unknown) => {
          if (mine !== generation.current) {
            return;
          }
          if (caught instanceof ApiError && caught.status === 401) {
            signOut('expired');
            return;
          }
          setStatus('failed');
        });
    },
    [signOut]
  );

  /**
   * EGY ÍRÁSI KÖR — a `documentId` a záradékban utazik.
   *
   * Nem az állapotból olvassuk vissza: mire a válasz megjön, a kurzor rég
   * másik bizonylaton állhat, és az állapotból vett azonosító a MÁSIK papír
   * puskáját írná.
   */
  const run = useCallback(
    async (id: string, drafts: DraftRow[]) => {
      const isCurrent = () => latest.current.documentId === id;
      // A „Mentés…" csak akkor, ha tényleg megy kérés — a változatlan puskán a
      // `Tab` ne villantson.
      const outcome = await writeDrafts(id, drafts, known.current, assigned.current, () => {
        if (isCurrent()) {
          setSaving('saving');
        }
      });
      if (outcome === 'expired') {
        signOut('expired');
        return;
      }

      // CSAK AZ AKTUÁLIS BIZONYLAT ÁLLAPOTÁT ÍRJUK. Egy elhagyott papír kimenő
      // mentése nem rendezheti át azt, amin a könyvelő MOST dolgozik.
      const saved = known.current.get(id);
      if (outcome === 'nothing' || !isCurrent() || saved === undefined) {
        return;
      }
      if (outcome !== 'done') {
        setSaving('failed');
        return;
      }

      setSheet(saved);
      setRows((previous) => withConfirmedIds(previous, saved, assigned.current));
      setSaving('saved');
    },
    [signOut]
  );

  const enqueue = useCallback(
    (id: string, drafts: DraftRow[]) => {
      // A `.catch` A LÁNCOT VÉDI: egy elutasított láncszem után minden
      // további `then` némán kimaradna — a mentés elakadna, és semmi nem szólna.
      chain.current = chain.current.then(() => run(id, drafts)).catch(() => undefined);
    },
    [run]
  );

  useEffect(() => {
    if (documentId === null) {
      setSheet(null);
      setRows([]);
      setRowsFor(null);
      setStatus('ready');
      return;
    }

    load(documentId);

    // A TAKARÍTÓ ÁG A KIÜRÍTÉS. Ez fut le, amikor a `documentId` VÁLTOZIK —
    // tehát pontosan akkor, amikor a panel leválik anélkül, hogy a mezők
    // fókuszvesztést kapnának.
    //
    // ⚠️ AZ AZONOSÍTÓ AZ EFFEKT SAJÁTJÁBÓL JÖN, NEM A REFBŐL. Mire a
    // takarítás lefut, a ref MÁR AZ ÚJ bizonylatot mutatja (a render
    // előbb frissíti), a `rows` viszont még a RÉGI papír gépelését tartja —
    // a betöltés nem ért vissza. Refből olvasva tehát az ELŐZŐ bizonylat
    // firkálását írnánk rá a KÖVETKEZŐRE. Nem adatvesztés: adatcsere, és
    // némán. *(A horog tesztje fogta meg, 09-03.)*
    //
    // A sorok viszont KELL, hogy refből jöjjenek: az effekt closure-e a
    // gépelés ELŐTTI állapotot látná, és pont az utolsó leütés a tét.
    const mine = documentId;
    return () => {
      if (latest.current.rowsFor === mine) {
        enqueue(mine, latest.current.rows);
      }
    };
  }, [documentId, load, enqueue]);

  const setCell = useCallback(
    (key: string, field: 'accountCode' | 'amount' | 'note', value: string) => {
      setRows((previous) =>
        withTrailingBlank(
          previous.map((row) => (row.key === key ? { ...row, [field]: value } : row))
        )
      );
      // A KORÁBBI NYUGTA ELTŰNIK. Egy „Mentve" felirat egy azóta átírt sor
      // fölött azt állítaná, hogy a MOSTANI szöveg van a szerveren.
      setSaving('idle');
    },
    []
  );

  /**
   * A TÖRLÉS MAGA MENT. Egy utána hívott `commit` még a törlés ELŐTTI sorokat
   * látná — a `setRows` csak a következő renderben ér a refbe —, és a sor
   * csak a következő fókuszvesztéskor tűnne el a szerverről.
   */
  const removeRow = useCallback(
    (key: string) => {
      const { documentId: id, rows: current, rowsFor: owner } = latest.current;
      const next = withTrailingBlank(current.filter((row) => row.key !== key));
      latest.current = { documentId: id, rows: next, rowsFor: owner };
      setRows(next);
      setSaving('idle');
      if (id !== null && owner === id) {
        enqueue(id, next);
      }
    },
    [enqueue]
  );

  /**
   * AZ ÚJ JELÖLŐ A ZÁRÓ ÜRES SOR ELÉ kerül, nem utána: a lista végén mindig
   * üres sornak kell állnia. Refbe is írjuk, mert a hívó rögtön a mezőre
   * fókuszál, és az ottani `blur` már ezeket a sorokat kell, hogy lássa.
   */
  const placePin = useCallback((pin: ProposalPin): string | null => {
    const { documentId: id, rows: current, rowsFor: owner } = latest.current;
    if (id === null || owner !== id) {
      return null;
    }
    const placed: DraftRow = { ...emptyRow(nextKey()), pin };
    const filled = current.filter(isFilled);
    const next = withTrailingBlank([...filled, placed]);
    latest.current = { documentId: id, rows: next, rowsFor: owner };
    setRows(next);
    setSaving('idle');
    return placed.key;
  }, []);

  /**
   * AZ ÁTHELYEZÉS MAGA MENT — ugyanazért, amiért a törlés: nincs utána mező,
   * amit el lehetne hagyni. Egy húzás egy kérés, a végén.
   */
  const movePin = useCallback(
    (key: string, pin: ProposalPin | null) => {
      const { documentId: id, rows: current, rowsFor: owner } = latest.current;
      const next = withTrailingBlank(
        current.map((row) => (row.key === key ? { ...row, pin } : row))
      );
      latest.current = { documentId: id, rows: next, rowsFor: owner };
      setRows(next);
      setSaving('idle');
      if (id !== null && owner === id) {
        enqueue(id, next);
      }
    },
    [enqueue]
  );

  const commit = useCallback(() => {
    const now = latest.current;
    if (now.documentId !== null && now.rowsFor === now.documentId) {
      // A KIÜRÜLT SOR A MEZŐ ELHAGYÁSAKOR KIESIK (`dropEmptyRows`, #68) — a
      // ref is rögtön, mert az utána jövő `blur`-ök már ezt kell lássák. A
      // `blur` alatt a fókusz még sehol sem áll; az `Enter`-nél a mezőben.
      const focusedId = document.activeElement?.id ?? '';
      const focused = now.rows.find((row) => noteFieldId(row.key) === focusedId);
      const rows = dropEmptyRows(now.rows, focused?.key ?? null);
      if (rows.length !== now.rows.length) {
        latest.current = { ...now, rows };
        setRows(rows);
      }
      enqueue(now.documentId, rows);
    }
  }, [enqueue]);

  return {
    status,
    rows,
    grossAmount: sheet?.grossAmount ?? null,
    allocated: sumAmounts(rows),
    saving,
    setCell,
    removeRow,
    commit,
    pinNumbers: pinNumbers(rows),
    placePin,
    movePin,
    reload: () => (documentId === null ? undefined : load(documentId)),
  };
}
